// Product brief for Nationwide CareMatters Annuity (2943 / 2944).
// See cmTwo.ts for the format intent. Page citations are the PRINTED label on
// the carrier illustration; the full write-up with ledger extracts lives in
// calculations/docs/nationwide-carematters-annuity/README.md.

export const careMattersAnnuityBrief = `**Nationwide CareMatters Annuity** — an annuity chassis
(calculationType \`annuityHybrid\`), despite sharing the CareMatters name with the
life-funded CareMatters II. One single premium buys a contract value; the LTC
benefit is a multiple of that value. Covers the single-life type and the joint
type, which differs only in its acceleration window.

**Contract value drives everything** (illustration p.6-7, "Tabular Detail"):
- \`CV(n) = CV(n-1) + interestCredit - riderCharges\`, both applied monthly, at a
  guaranteed 3.00%.
- The rider charge is a flat percentage of contract value set by risk class
  (0.425% Standard / 0.385% Preferred). It is waived once a benefit has been paid
  in the PRIOR contract month (rider disclosure ICC25-NWLA-686.1 p.2), so a
  claim's first month is still charged and the month after benefits stop is not.

**Two pools** (p.5):
- \`base_pool\` is the **LTC Acceleration** layer — always 1x contract value, over
  24 months single / 36 joint.
- \`eob\` is the **Extension of Benefits** layer — 1x CV (Standard) or 2x CV
  (Preferred), over the remaining months.
- Totals are therefore 2x CV across 48/72 months single, 3x CV across 72/108
  joint. The acceleration window is constant across risk classes; only the
  extension lengthens.

**Monthly cap is not fixed at issue** — this is what makes the product its own
strategy:
- \`monthlyCap = contractValue / accelerationMonths\`, so the benefit grows before
  claim even with no inflation rider (p.5: "the maximum monthly benefit may
  increase if the annuity Contract Value increases").
- Day-1 monthly is identical in both risk classes — the multiple and the period
  scale together.

**Inflation rider is a FLOOR, not pool growth** (p.6 footnote): the cap is
\`max(CV / accelerationMonths, dayOneMonthly x 1.05^(n-1))\`. The contract-value
leg wins early; the inflation leg takes over at a crossover year that moves with
issue age. The rider is also a one-off year-1 charge against the contract value
(48,000 of a 100,000 premium), already baked into the value the strategy reads —
electing it LOWERS the day-1 benefit.

**Payout — always cash indemnity** (p.4). No restrictions on use; 100% may fund
informal care. A fixed product property, not a user election.

**Elimination period — 90 days, retroactive** (p.5): the withheld days are
reimbursed as a lump on the row that satisfies the EP, making the first payment
4x monthly. Days funded are those actually withheld from payment, so an
asymmetric home/facility claim does not pay the same day twice.

**Death benefit** = the remaining contract value (\`db\`). Acceleration draws
deplete it dollar for dollar and guaranteed interest can grow it mid-claim, so it
erodes to zero across the first 24/36 claim months — a claim reaching the
extension layer leaves beneficiaries nothing (p.9). There is **no guaranteed
minimum**; \`policyMinimumGuaranteedBenefitAmount\` is ForeCare's floor and stays
null here.
`;
